import { putOrderTracking } from '../store/action/OrderAction';

/**
 * The single queue every `trackingAttrs` write goes through (ELNKWEB-3215).
 *
 * `PUT /order/:id` merges `trackingAttrs` server-side by reading the stored
 * value, merging, and writing back - with no row lock. Two overlapping writes
 * therefore both merge over the pre-both snapshot and the loser's key is
 * silently dropped. Serializing here is what stops that, so EVERY tracking
 * writer must use this function rather than calling the API itself: a second
 * writer with its own chain races the first, which is exactly the bug this
 * replaced (`.com` writes a DNI-sourced `cpid` on the same trigger that produces
 * the first `displayedTFN`).
 *
 * Module-level rather than a hook ref on purpose - a component remount would
 * reset a ref and reopen the race.
 *
 * Kept byte-identical with the copies in the sibling checkouts -
 * app-checkout.earthlink.net and app-checkout.earthlink.com - so diff them before
 * and after editing this file. A change here that lands in only one site is how
 * the three drifted apart in the first place. Rationale and the full contract:
 * ELNKWEB-3215.
 */
let chain = Promise.resolve();

/**
 * Queue one tracking write. Never rejects - a tracking failure must not surface
 * to the customer or interrupt checkout.
 *
 * Resolves to WHETHER the write landed rather than rejecting. Callers need that
 * signal - a caller that dedupes against the last value sent has to know not to
 * treat a failed write as sent - but a rejection here would surface as an
 * unhandled one in the middle of checkout. Never rejects.
 *
 * @param {object} trackingAttrs keys to merge onto the order.
 * @param {string} orderAccessToken the order to write to (its access token, never the numeric id).
 * @returns {Promise<boolean>} true when the write landed, false when it failed.
 */
export const queueOrderTrackingWrite = (trackingAttrs, orderAccessToken) => {
  chain = chain
    .catch(() => false)
    .then(() =>
      Promise.resolve(putOrderTracking({ trackingAttrs }, orderAccessToken))
        .then(() => true)
        .catch(() => false),
    );
  return chain;
};

/** Test seam: drop any queued work so one test cannot leak into the next. */
export const resetOrderTrackingQueue = () => {
  chain = Promise.resolve();
};
